import { useSyncExternalStore } from 'react';
import logger from '@ayocore/platform/common/helpers/logger';
import { AuthController } from '@ayocore/platform/identity/client/AuthController';
import { useActivityThreadRepository } from '@ayocore/activity/client/rpc';
import { crmActivityChannel } from '@ayocore/activity/client/messaging';

/**
 * Состояние бейджа Activity-дока.
 *
 * `attentionCount` — число тредов, где есть непрочитанные сообщения,
 * адресованные текущему пользователю (mention, assignment и т.п.).
 * Источник — `ActivityThreadRepository.getMyQuickFilterCounters().attention`.
 */
type ActivityBadgeState = {
  attentionCount: number;
  isLoaded: boolean;
};

const INITIAL_STATE: ActivityBadgeState = {
  attentionCount: 0,
  isLoaded: false,
};

const RELOAD_DEBOUNCE_MS = 300;

/**
 * Глобальный синглтон-кеш счётчика «адресованных мне» тредов для
 * Activity-дока. Живёт независимо от mount'а Activity-панели: подписывается
 * на WS-канал `crm-activity` и AuthController, чтобы бейдж был актуален
 * даже когда панель закрыта.
 *
 * Паттерн: Controller + `useSyncExternalStore` (как UsersController,
 * AuthController, NotificationController).
 */
export class ActivityBadgeController {
  private static instance: ActivityBadgeController | null = null;

  private listeners = new Set<() => void>();
  private state: ActivityBadgeState = INITIAL_STATE;
  private reloadTimer: ReturnType<typeof setTimeout> | null = null;
  private unsubscribeAuth: (() => void) | null = null;
  private unsubscribeChannel: (() => void) | null = null;

  static getInstance(): ActivityBadgeController {
    if (!ActivityBadgeController.instance) {
      ActivityBadgeController.instance = new ActivityBadgeController();
    }
    return ActivityBadgeController.instance;
  }

  private constructor() {
    const auth = AuthController.getInstance();

    // На login — грузим счётчики, на logout — сбрасываем.
    // Подписка делается один раз, дальше всё на event'ах.
    this.unsubscribeAuth = auth.subscribe(() => {
      const snapshot = auth.getSnapshot();
      if (snapshot.isAuthenticated && snapshot.user) {
        void this.loadCounters();
      } else {
        this.replaceState(INITIAL_STATE);
      }
    });

    // Если пользователь уже залогинен (страница перезагружена) — грузим сразу.
    const authSnapshot = auth.getSnapshot();
    if (authSnapshot.isAuthenticated && authSnapshot.user) {
      void this.loadCounters();
    }

    // WS — на любое изменение тредов планируем reload с debounce'ом,
    // чтобы не дёргать API на каждое сообщение в burst'е.
    this.unsubscribeChannel = crmActivityChannel.onMessage(() => {
      this.scheduleReload();
    });
  }

  // ===== useSyncExternalStore API =====

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): ActivityBadgeState => this.state;

  private emit(): void {
    for (const l of this.listeners) l();
  }

  private replaceState(next: ActivityBadgeState): void {
    if (
      this.state.attentionCount === next.attentionCount &&
      this.state.isLoaded === next.isLoaded
    ) {
      return;
    }
    this.state = next;
    this.emit();
  }

  // ===== Загрузка =====

  private scheduleReload(): void {
    if (this.reloadTimer) clearTimeout(this.reloadTimer);
    this.reloadTimer = setTimeout(() => {
      this.reloadTimer = null;
      void this.loadCounters();
    }, RELOAD_DEBOUNCE_MS);
  }

  /**
   * Загружает агрегированные счётчики и берёт `attention` —
   * число тредов с непрочитанными адресованными мне сообщениями.
   */
  private async loadCounters(): Promise<void> {
    try {
      const repo = useActivityThreadRepository();
      const counters = await repo.getMyQuickFilterCounters();
      this.replaceState({
        attentionCount: Number(counters.attention ?? 0),
        isLoaded: true,
      });
    } catch (error) {
      logger.warn('[ActivityBadgeController] loadCounters failed', { error });
    }
  }

  /**
   * Принудительный reload (например, после mark-as-read из Activity-панели).
   */
  refresh(): void {
    void this.loadCounters();
  }

  dispose(): void {
    if (this.reloadTimer) {
      clearTimeout(this.reloadTimer);
      this.reloadTimer = null;
    }
    this.unsubscribeAuth?.();
    this.unsubscribeChannel?.();
    this.unsubscribeAuth = null;
    this.unsubscribeChannel = null;
    ActivityBadgeController.instance = null;
  }
}

/**
 * Реактивное число «адресованных мне» тредов для бейджа Activity-дока.
 */
export function useActivityAttentionCount(): number {
  const controller = ActivityBadgeController.getInstance();
  const state = useSyncExternalStore(
    controller.subscribe,
    controller.getSnapshot,
    controller.getSnapshot
  );
  return state.attentionCount;
}
